import React from "react";
import {
  Brain, Loader2, RefreshCw, Printer, TrendingUp, ThumbsUp,
  AlertOctagon, AlertTriangle, CheckCircle, Shield, Sparkles, Zap
} from "lucide-react";

/**
 * Panel de análisis Pando de una oferta.
 * Props: aiAnalysis, analyzingAi, runAiAnalysis, handlePrintReport,
 *        aiModel, setAiModel, aiModelsCatalog
 */
export function OfferAISummary({
  aiAnalysis, analyzingAi, runAiAnalysis, handlePrintReport,
  aiModel, setAiModel, aiModelsCatalog = [],
  includeInternalHours = true, setIncludeInternalHours,
}) {
  const selectedModel = aiModelsCatalog.find(m => m.id === aiModel);
  const tierIcon = selectedModel?.tier === "premium" ? <Sparkles size={11}/> :
                   selectedModel?.tier === "fast" ? <Zap size={11}/> :
                   <Brain size={11}/>;
  const tierColor = selectedModel?.tier === "premium" ? "from-amber-50 to-yellow-50 border-amber-300 text-amber-800" :
                    selectedModel?.tier === "fast" ? "from-sky-50 to-cyan-50 border-sky-300 text-sky-800" :
                    "from-emerald-50 to-green-50 border-emerald-300 text-emerald-800";
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="ai-analysis-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Brain size={18} className="text-[#007934]" />
          <div>
            <h3 className="font-bold text-gray-900 font-['Outfit'] text-sm">Análisis Pando · Valoración Técnico-Económica</h3>
            {aiAnalysis?.generated_at && (
              <p className="text-xs text-gray-400">
                Generado {new Date(aiAnalysis.generated_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })} por {aiAnalysis.generated_by}
                {aiAnalysis.model_label && <span className="ml-1 text-purple-600">· {aiAnalysis.model_label}</span>}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Selector de modelo Pando */}
          {setAiModel && aiModelsCatalog.length > 0 && (
            <div className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg border bg-gradient-to-r text-[11px] font-bold ${tierColor}`}>
              {tierIcon}
              <select
                value={aiModel}
                onChange={(e) => setAiModel(e.target.value)}
                disabled={analyzingAi}
                data-testid="ai-model-selector"
                className="bg-transparent border-0 outline-none font-bold cursor-pointer pr-1"
                title="Elige el modelo Pando. Sonnet=equilibrado, Opus=premium, Haiku=rápido"
              >
                {aiModelsCatalog.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.label}{m.tier === "premium" ? "  ★" : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
          {aiAnalysis && !analyzingAi && (
            <button onClick={handlePrintReport} data-testid="print-ai-report-btn"
              className="flex items-center gap-2 border border-gray-200 text-gray-600 px-4 py-2 rounded-xl text-sm font-semibold hover:bg-gray-50 shadow-sm transition-all">
              <Printer size={14} /> Imprimir PDF
            </button>
          )}
          {/* Toggle Incluir horas internas IT */}
          {setIncludeInternalHours && (
            <label
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[11px] font-semibold cursor-pointer transition-colors ${
                includeInternalHours
                  ? "bg-[#E8F5E9] border-[#007934]/30 text-[#007934]"
                  : "bg-gray-50 border-gray-200 text-gray-500"
              } ${analyzingAi ? "opacity-50 cursor-not-allowed" : ""}`}
              data-testid="toggle-internal-hours"
              title="Si está activo, el análisis Pando añadirá secciones CAPEX/OPEX con horas internas de IT (integración, mantenimiento, operaciones) calibradas al tamaño de la oferta">
              <input
                type="checkbox"
                checked={includeInternalHours}
                disabled={analyzingAi}
                onChange={e => setIncludeInternalHours(e.target.checked)}
                className="w-3.5 h-3.5 cursor-pointer accent-[#007934]"
              />
              + Horas internas IT
            </label>
          )}
          <button onClick={() => runAiAnalysis()} disabled={analyzingAi} data-testid="run-ai-analysis-btn"
            className="flex items-center gap-2 bg-[#007934] text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-[#006528] disabled:opacity-60 shadow-sm transition-all">
            {analyzingAi ? <Loader2 size={14} className="animate-spin" /> : aiAnalysis ? <RefreshCw size={14} /> : <Brain size={14} />}
            {analyzingAi ? "Analizando…" : aiAnalysis ? "Regenerar" : "Generar análisis Pando"}
          </button>
        </div>
      </div>

      {analyzingAi && (
        <div className="flex items-center gap-3 py-8 justify-center text-gray-400">
          <Loader2 size={20} className="animate-spin text-[#007934]" />
          <span className="text-sm">La Pando está analizando la oferta… (puede tardar hasta 30 segundos)</span>
        </div>
      )}

      {!analyzingAi && !aiAnalysis && (
        <div className="text-center py-8 text-gray-400">
          <Brain size={28} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">Pulsa "Generar análisis Pando" para obtener una valoración técnico-económica de esta oferta.</p>
        </div>
      )}

      {!analyzingAi && aiAnalysis && (
        <div className="space-y-4">
          <div className="bg-[#E8F5E9] rounded-xl p-4 border-l-4 border-[#007934]">
            <p className="text-xs font-bold text-[#007934] uppercase tracking-wider mb-1">Resumen Ejecutivo</p>
            <p className="text-sm text-gray-800 leading-relaxed">{aiAnalysis.resumen_ejecutivo}</p>
          </div>

          {aiAnalysis.valoracion_economica && (
            <div className="flex items-start gap-3 bg-gray-50 rounded-xl p-4">
              <TrendingUp size={18} className={
                aiAnalysis.valoracion_economica.nivel === "Adecuado" ? "text-green-600" :
                aiAnalysis.valoracion_economica.nivel === "Ajustado" ? "text-blue-600" :
                aiAnalysis.valoracion_economica.nivel === "Elevado"  ? "text-amber-600" : "text-red-600"
              } />
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-xs font-bold text-gray-700 uppercase tracking-wider">Valoración Económica</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                    aiAnalysis.valoracion_economica.nivel === "Adecuado" ? "bg-green-100 text-green-700" :
                    aiAnalysis.valoracion_economica.nivel === "Ajustado" ? "bg-blue-100 text-blue-700" :
                    aiAnalysis.valoracion_economica.nivel === "Elevado"  ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"
                  }`}>{aiAnalysis.valoracion_economica.nivel}</span>
                </div>
                <p className="text-sm text-gray-600">{aiAnalysis.valoracion_economica.justificacion}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 print:block print:space-y-3">
            {aiAnalysis.analisis_capex && (
              <div className="bg-white border border-gray-100 rounded-xl p-4">
                <p className="text-xs font-bold text-[#007934] uppercase tracking-wider mb-2 flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-[#007934]"/>Análisis CAPEX</p>
                <p className="text-sm text-gray-600 leading-relaxed">{aiAnalysis.analisis_capex}</p>
              </div>
            )}
            {aiAnalysis.analisis_opex && (
              <div className="bg-white border border-gray-100 rounded-xl p-4">
                <p className="text-xs font-bold text-teal-700 uppercase tracking-wider mb-2 flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-teal-500"/>Análisis OPEX</p>
                <p className="text-sm text-gray-600 leading-relaxed">{aiAnalysis.analisis_opex}</p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 print:block print:space-y-3">
            {aiAnalysis.puntos_fuertes?.length > 0 && (
              <div className="bg-green-50 rounded-xl p-4">
                <p className="text-xs font-bold text-green-700 uppercase tracking-wider mb-2 flex items-center gap-1.5"><ThumbsUp size={12}/>Puntos Fuertes</p>
                <ul className="space-y-1.5">
                  {aiAnalysis.puntos_fuertes.map((p, i) => (
                    <li key={`pf-${i}`} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle size={13} className="text-green-500 flex-shrink-0 mt-0.5" />{p}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {aiAnalysis.puntos_riesgo?.length > 0 && (
              <div className="bg-amber-50 rounded-xl p-4">
                <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-2 flex items-center gap-1.5"><AlertOctagon size={12}/>Puntos de Riesgo</p>
                <ul className="space-y-1.5">
                  {aiAnalysis.puntos_riesgo.map((p, i) => (
                    <li key={`pr-${i}`} className="flex items-start gap-2 text-sm text-gray-700">
                      <AlertTriangle size={13} className="text-amber-500 flex-shrink-0 mt-0.5" />{p}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {aiAnalysis.recomendaciones_tic?.length > 0 && (
            <div className="bg-blue-50 rounded-xl p-4">
              <p className="text-xs font-bold text-blue-700 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Shield size={12}/>Recomendaciones para el Validador TIC</p>
              <ul className="space-y-1.5">
                {aiAnalysis.recomendaciones_tic.map((r, i) => (
                  <li key={`rt-${i}`} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="w-5 h-5 rounded-full bg-blue-200 text-blue-700 text-[10px] font-bold flex items-center justify-center flex-shrink-0">{i+1}</span>{r}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(aiAnalysis.comparativa_mercado || aiAnalysis.conclusion) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 print:block print:space-y-3">
              {aiAnalysis.comparativa_mercado && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">Comparativa de Mercado</p>
                  <p className="text-sm text-gray-600 leading-relaxed">{aiAnalysis.comparativa_mercado}</p>
                </div>
              )}
              {aiAnalysis.conclusion && (
                <div className="bg-gray-900 rounded-xl p-4">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Conclusión</p>
                  <p className="text-sm text-white leading-relaxed">{aiAnalysis.conclusion}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
